import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { ContainerWrapper } from "../../../components/styles";
import apiClient from "../../../utils/clients/axios";
import { handleToastMsg } from "../../../components/Toast";
import NotFound from "../../../components/NotFound";
import { solutionImages } from "../../../utils/solutionImages";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NOT_FOUND = "Task sheet not found";

// Where one manager's solution stands in the approval flow
const approvalStatus = (item) => {
  if (item?.accepted === true) return { label: "Accepted", color: "#2f9e44" };
  if (item?.accepted === false) return { label: "Declined", color: "#e03131" };
  if (item?.solution || item?.image) {
    return { label: "Awaiting approval", color: "#f08c00" };
  }
  return { label: "Solution not submitted", color: "#868e96" };
};

// Gallery for one task sheet issue; linked from the Task Sheets Excel report
function TaskSheetImages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // { src, name } of the image open in the full-size dialog
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchIssue = async () => {
      // Issue ids are UUIDs; anything else would make the API error with a 500
      if (!UUID_PATTERN.test(id || "")) {
        setError(NOT_FOUND);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await apiClient.get(
          `api/quality_control/report_issue/${id}`
        );
        setIssue(response?.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          // Stored session is stale (expired/invalid token): log in again
          // and come back here
          localStorage.clear();
          navigate("/login", {
            replace: true,
            state: { from: location, sessionExpired: true },
          });
          return;
        }
        setError(
          err?.response?.status === 404 ? NOT_FOUND : "Unable to load task sheet"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchIssue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch as a blob so the browser saves the file instead of opening it;
  // works for /uploads URLs and legacy base64 data URIs alike
  const handleDownload = async (src, name) => {
    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const extension = (blob.type.split("/")[1] || "jpg").replace(
        "jpeg",
        "jpg"
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `task-sheet-${issue?.slNo}-${name}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Task sheet image download error:", err);
      handleToastMsg("Unable to download image", "error");
    }
  };

  const renderImages = (images, namePrefix) => (
    <Grid container gap={2}>
      {images.map((src, index) => (
        <Grid item key={index} style={{ textAlign: "center" }}>
          <img
            src={src}
            alt={`task sheet ${index + 1}`}
            loading="lazy"
            style={{
              width: "220px",
              height: "180px",
              objectFit: "cover",
              cursor: "pointer",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
            onClick={() =>
              setSelectedImage({ src, name: `${namePrefix}-${index + 1}` })
            }
          />
          <Grid container justifyContent="center" alignItems="center" gap={1}>
            <a href={src} target="_blank" rel="noreferrer">
              Open original
            </a>
            <Tooltip title="Download image">
              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                  handleDownload(src, `${namePrefix}-${index + 1}`)
                }
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );

  if (loading) {
    return (
      <ContainerWrapper container justifyContent="center">
        <CircularProgress />
      </ContainerWrapper>
    );
  }

  if (error === NOT_FOUND || (!error && !issue)) {
    return (
      <NotFound message="This task sheet doesn`t exist or the link is invalid. Please check the link in the report." />
    );
  }

  if (error) {
    return (
      <ContainerWrapper container>
        <h3>{error}</h3>
      </ContainerWrapper>
    );
  }

  const reportedImages = Array.isArray(issue?.image)
    ? issue.image.filter(Boolean)
    : [];
  const solutions = issue?.reportingManagerSolution || [];

  // Seek date / reason rows for the summary, only where a manager gave them;
  // labelled with the manager's name when the issue has several managers
  const seekRows = solutions.flatMap((item, index) => {
    const who =
      solutions.length > 1 ? ` (${item?.name || `Manager ${index + 1}`})` : "";
    return [
      item?.seekedDate && {
        key: `seek-date-${index}`,
        label: `Seeked Date${who}`,
        value: dayjs(item.seekedDate).format("DD-MM-YYYY"),
      },
      item?.seekReason && {
        key: `seek-reason-${index}`,
        label: `Seek Reason${who}`,
        value: item.seekReason,
      },
    ].filter(Boolean);
  });

  return (
    <ContainerWrapper container rowGap={3}>
      <Grid item xs={12}>
        <h2>Task Sheet #{issue?.slNo} - Images</h2>
      </Grid>

      <Grid item xs={12} md={8}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Hall</TableCell>
              <TableCell>{issue?.hall}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Issue Date</TableCell>
              <TableCell>
                {dayjs(issue?.created_at).format("DD-MM-YYYY, hh:mm A")}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Reported By</TableCell>
              <TableCell>{issue?.reportedBy}</TableCell>
            </TableRow>
            {issue?.employeeName && (
              <TableRow>
                <TableCell>Employee Name</TableCell>
                <TableCell>{issue.employeeName}</TableCell>
              </TableRow>
            )}
            {issue?.machineName && (
              <TableRow>
                <TableCell>Machine Name</TableCell>
                <TableCell>{issue.machineName}</TableCell>
              </TableRow>
            )}
            {issue?.reason && (
              <TableRow>
                <TableCell>Reason</TableCell>
                <TableCell>{issue.reason}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell>Department</TableCell>
              <TableCell>{issue?.department?.join(", ")}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>{issue?.status}</TableCell>
            </TableRow>
            {issue?.isEmergency && (
              <TableRow>
                <TableCell>Emergency</TableCell>
                <TableCell style={{ color: "#e03131", fontWeight: 700 }}>
                  YES
                </TableCell>
              </TableRow>
            )}
            {seekRows.map(({ key, label, value }) => (
              <TableRow key={key}>
                <TableCell>{label}</TableCell>
                <TableCell
                  style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                >
                  {value}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell style={{ wordBreak: "break-word" }}>
                {issue?.description}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Grid>

      <Grid item xs={12}>
        <h3>Reported Images ({reportedImages.length})</h3>
        {reportedImages.length > 0 ? (
          renderImages(reportedImages, "reported")
        ) : (
          <p>No Image</p>
        )}
      </Grid>

      {solutions.length > 0 && (
        <Grid item xs={12}>
          <h3>Solutions ({solutions.length})</h3>
          {solutions.map((item, index) => {
            const approval = approvalStatus(item);
            return (
              <Grid item xs={12} key={index} mb={4}>
                <h4 style={{ margin: "8px 0" }}>
                  {index + 1}. {item?.name || `Manager ${index + 1}`}
                </h4>
                <Grid item xs={12} md={8}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ width: "180px" }}>Solution</TableCell>
                        <TableCell
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {item?.solution || "Not submitted yet"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Solution Date</TableCell>
                        <TableCell>
                          {item?.solutionDate
                            ? dayjs(item.solutionDate).format(
                                "DD-MM-YYYY, hh:mm A"
                              )
                            : "NA"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Approval</TableCell>
                        <TableCell
                          style={{ color: approval.color, fontWeight: 600 }}
                        >
                          {approval.label}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Times Declined</TableCell>
                        <TableCell>{item?.rejectedCount ?? 0}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
                <p style={{ fontWeight: 600, margin: "12px 0 8px" }}>
                  Solution Images ({solutionImages(item).length})
                </p>
                {solutionImages(item).length > 0 ? (
                  renderImages(solutionImages(item), `solution-${index + 1}`)
                ) : (
                  <p>No Image</p>
                )}
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
      >
        <DialogTitle>
          <Tooltip title="Download image">
            <IconButton
              aria-label="download"
              color="primary"
              onClick={() =>
                handleDownload(selectedImage?.src, selectedImage?.name)
              }
              style={{ position: "absolute", right: 56, top: 8 }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="close"
            onClick={() => setSelectedImage(null)}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <img
            src={selectedImage?.src}
            alt="task sheet"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>
    </ContainerWrapper>
  );
}

export default TaskSheetImages;
